import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "reactstrap";
import PlanTierCard from "./plans/PlanTierCard";
import { planCardData } from "../utils/data";
import SpinnerIcon from "./global/SpinnerIcon";
import {
  getUserSubscription,
  resumeSubscription,
} from "../store/actions/subscribeAction";

/*
    The pricing grid: the three subscribable tiers, left to right, stacking on
    small screens. Static Data is deliberately not here — it is a one-time
    purchase with its own panel below the grid (StaticDataPanel), and the
    catalogue marks it `listed: false`.

    This component owns the Stripe subscription lookup, because that answer
    decides what EVERY card's button says; the cards themselves are purely
    presentational.

    Gone from the previous version, both on purpose: the mobile "Show
    Features" toggle (the design lists features on every card at every width,
    so the resize listener and the per-card `active` flag it drove are no
    longer needed) and the UncontrolledTooltip information hovers.
*/

/** The plans that are actually for sale as monthly subscriptions. */
const TIERS = planCardData.filter((plan) => plan.listed !== false);

const PlanCard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user, authLoading } = useSelector((state) => state.authUser);
  const [subscriptionDetails, setSubscriptionDetails] = useState("");
  // True once GET current-subscription has answered, including "No
  // subscription" (which arrives with no data).
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [resuming, setResuming] = useState(false);

  const currentSubscription = subscriptionDetails?.currentSubscription;
  const currentPlanId = currentSubscription?.plan_id;
  const nextPlanId = subscriptionDetails?.subscriptionSchedule?.plan_id;

  /*
    A cancelled plan that is still paid for can be resumed (static#104).

    Cancelling sets the subscription to end with the paid period, so Stripe
    still calls it active and `cancel_at` holds the end date. That used to
    read "Your Current Plan", disabled, and checkout refuses a plan you
    already have — so the customer had no way back until it ran out.

    Not when a schedule exists: a downgrade ALSO sets `cancel_at` (the old
    plan ends, the scheduled one starts), and resuming would leave the
    customer on two plans. The backend refuses that case too; the grid keeps
    showing Current / Next for it, as before.
  */
  const resumablePlanId =
    currentSubscription?.cancel_at && !subscriptionDetails?.subscriptionSchedule
      ? currentPlanId
      : null;
  const isResumable = (planId) =>
    Boolean(resumablePlanId) && planId === resumablePlanId;

  /*
    A plan is "yours" only while Stripe has a subscription for it
    (golfbert-api#31).

    This used to also trust users.api_plan, which is the last plan the
    customer chose. Nothing clears it when a subscription ends — backend#95
    pauses the key and keeps the plan so the same key can come back — so a
    customer who cancelled found the button for the plan they had disabled
    for good, and could not subscribe to it again.

    api_plan is still used in one case: the customer has a Stripe customer
    but the subscription lookup never answered (a failed request). Guessing
    "not subscribed" there would let a paying customer buy their own plan a
    second time. A customer with no Stripe customer cannot have a
    subscription, so there is nothing to wait for.
  */
  const subscriptionKnown = !user?.stripe_customer_id || subscriptionChecked;
  const isCurrentPlan = (planId) =>
    planId === currentPlanId ||
    (!subscriptionKnown &&
      user?.api_plan === planId?.replace(/^api-/g, "").trim());

  const lookUpSubscription = () =>
    dispatch(
      getUserSubscription(user?.stripe_customer_id, (res) => {
        setSubscriptionDetails(res);
        setSubscriptionChecked(true);
      })
    );

  useEffect(() => {
    if (user?.stripe_customer_id) lookUpSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Asks again on success, so the card turns back into "Your Current Plan"
  // from what Stripe now says rather than from a guess.
  const resumePlan = async () => {
    setResuming(true);
    const resumed = await dispatch(resumeSubscription());
    setResuming(false);
    if (resumed) lookUpSubscription();
  };

  /*
      Checkout reads the chosen plan from router state rather than the URL —
      see `location.state` in components/global/Subscribe.js. `priceId` is the
      catalogue id for every plan except the sample one, which checkout knows
      as "test".
  */
  const selectPlan = (plan) =>
    history.push({
      pathname: plan.path,
      state: {
        price: plan.unit_amount,
        month: plan.recurring?.interval,
        priceId: plan.priceId || plan.id,
      },
    });

  /*
      The sample tier is for accounts that have never had a plan. Checkout
      sends it as priceId "test", and the backend's free path sets
      api_plan = "test" without looking at Stripe, so a paying customer who
      clicked it would lose their paid access while Stripe went on billing
      them. This is the rule the old page used to hide its free button:
      api_plan "test" means they are already on it, and any Stripe customer
      has (or had) a paid plan.
  */
  const onSamplePlan = user?.api_plan === "test" && !user?.stripe_customer_id;
  const sampleClosed = (plan) =>
    plan.id === "api-test" &&
    Boolean(user?.api_plan === "test" || user?.stripe_customer_id);

  const labelFor = (plan) => {
    if (isResumable(plan.id)) {
      return resuming ? (
        <>
          <SpinnerIcon label="Resuming your plan" /> Resuming
        </>
      ) : (
        "Resume plan"
      );
    }
    if (isCurrentPlan(plan.id)) return "Your Current Plan";
    if (plan.id === nextPlanId) return "Your Next Plan";
    if (plan.id === "api-test" && onSamplePlan) return "Your Current Plan";
    if (sampleClosed(plan)) return "Not available";
    return plan.btnText;
  };

  if (authLoading) {
    return (
      <div className="text-center">
        <Spinner> </Spinner>
      </div>
    );
  }

  return (
    <div className="plans-grid">
      {TIERS.map((plan) => (
        <PlanTierCard
          key={plan.id}
          plan={plan}
          buttonLabel={labelFor(plan)}
          disabled={
            isResumable(plan.id)
              ? resuming
              : isCurrentPlan(plan.id) ||
                plan.id === nextPlanId ||
                sampleClosed(plan)
          }
          onSelect={isResumable(plan.id) ? resumePlan : selectPlan}
        />
      ))}
    </div>
  );
};

export default PlanCard;
